import instantsearch from 'instantsearch.js';
import {
    hits,
    hitsPerPage,
    sortBy,
    menu,
    stats,
    pagination,
    currentRefinements,
    clearRefinements,
    panel,
    searchBox,
    configure
} from 'instantsearch.js/es/widgets';

import _reduce from 'lodash/reduce';
import _flatMap from 'lodash/flatMap';
import _cloneDeep from 'lodash/cloneDeep';
import _unset from 'lodash/unset';

import clients from '@theme-modern-js/utils/Search/SearchClients';
import mutators from '@theme-modern-js/utils/Search/Mutators';
import widgets from '@theme-modern-js/utils/Search/Widgets';
import {executeHtmlHooks} from "@theme-modern-js/utils/htmlHooks";
import loader from '@theme-modern-js/modules/loader';

/**
 * @property {Function} loadingCallback
 */
export default class SearchWrapper {
    /**
     * @param {Object} config configuration for InstantSearch
     */
    constructor(config) {
        this.config = config;

        if (!this.config.hasOwnProperty('client')) {
            this.config.client = {driver: 'default'};
        } else if (!this.config.client.hasOwnProperty('driver')) {
            this.config.client.driver = 'default';
        } else if (!clients.has(this.config.client.driver)) {
            console.warn(`Client for [${this.config.client.driver}] not supported yet, use [default] instead`)
            this.config.driver = 'default';
        }

        /**
         * All refinements for current search.
         *
         * @type {Map<String, Object>}
         */
        this.refinementsMap = new Map();
        this.config.refinements?.forEach((refinement) => this.refinementsMap.set(refinement.attribute ?? refinement.label, refinement));

        /**
         * Current search Client
         *
         * @type {Object}
         */
        this.client = clients.get(this.config.client.driver)(this.config.client.config);

        /**
         * Define initialState for the search client. See https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-initialuistate
         *
         * @type {Object}
         */
        const initialState = {
            ...config.initialState ?? {},
            query: config.query
        };

        const routing = config.routing
            ? {
                stateMapping: this.stateMapping(config.indexName),
            }
            : false;

        this.search = instantsearch({
            searchClient: this.client,
            indexName: config.indexName,
            routing,
            initialUiState: {
                [config.indexName]: initialState,
            }
        });

        this.loadingCallback = (isSearchStalled) => loader(isSearchStalled);

        const me = this;
        this.search.addWidgets([
            {
                render({searchMetadata = {}}) {
                    const {isSearchStalled} = searchMetadata;
                    me.loadingCallback(isSearchStalled === true);
                },
            },
        ])
    }

    /**
     * state mapping for URL generation
     * @param indexName
     * @returns {{}|{routeToState(*): {}, stateToRoute(*): *}|*}
     */
    stateMapping(indexName) {
        const refinementsMap = this.refinementsMap;

        return {
            stateToRoute(uiState) {
                const routeState = _cloneDeep(uiState[indexName]);

                // transformation to cleanup query variables
                refinementsMap.forEach((refinement, key) => {
                    if (refinement.uiStateType) { // if uiStateType is not set, the transformation for this refinement is not possible
                        if (refinement.type === 'hierarchicalMenu') {
                            // adapted from the handling in the "else" branch, but for each hierarchical attribute

                            if (routeState[refinement.uiStateType]) {
                                for (const attribute of refinement.instantSearchConfig.attributes) {
                                    if ((!(attribute in routeState)) && routeState[refinement.uiStateType][attribute]) {
                                        for (const innerKey in routeState[refinement.uiStateType]) {
                                            if (!(innerKey in routeState)) {
                                                routeState[innerKey] = routeState[refinement.uiStateType][innerKey];
                                                _unset(routeState[refinement.uiStateType], innerKey);
                                            }
                                        }
                                    }
                                }
                            }
                        } else if ((!(key in routeState)) && routeState[refinement.uiStateType]?.[key]) {
                            // move each key of this uiStateType to the object root of routeState, but only if they are not already set there
                            for (const innerKey in routeState[refinement.uiStateType]) {
                                if (!(innerKey in routeState)) {
                                    routeState[innerKey] = routeState[refinement.uiStateType][innerKey];
                                    _unset(routeState[refinement.uiStateType], innerKey);
                                }
                            }
                        }
                    }
                });

                return routeState;
            },
            routeToState(routeState) {
                const uiState = _cloneDeep(routeState);

                // reverse of the transformation above
                refinementsMap.forEach((refinement, key) => {
                    if (refinement.uiStateType) { // if uiStateType is not set, the transformation for this refinement is not possible
                        if (refinement.type === 'hierarchicalMenu') {
                            // adapted from the handling in the "else" branch, but for each hierarchical attribute

                            for (const attribute of refinement.instantSearchConfig.attributes) {
                                if (attribute in routeState) {
                                    if (typeof uiState.hierarchicalMenu === 'object') {
                                        if (!(attribute in uiState.hierarchicalMenu)) {
                                            uiState.hierarchicalMenu[attribute] = routeState[attribute];
                                        }
                                    } else {
                                        uiState.hierarchicalMenu = {
                                            [attribute]: routeState[attribute],
                                        };
                                    }
                                }
                            }
                        } else if (key in routeState) {
                            // copy each key of this uiStateType from the object root of routeState, but only if they are not already set in the uiStateType namespace
                            if (typeof uiState[refinement.type] === 'object') {
                                if (!(key in uiState[refinement.uiStateType])) {
                                    uiState[refinement.uiStateType][key] = routeState[key];
                                }
                            } else {
                                uiState[refinement.uiStateType] = {
                                    [key]: routeState[key],
                                };
                            }
                        }
                    }
                });

                return {
                    [indexName]: uiState,
                };
            },
        };
    }

    /**
     * Define additional or overwrite existing mutator.
     *
     * @param {String} key
     * @param {Function} callback
     */
    defineAdditionalMutator(key, callback) {
        mutators.set(key, callback);
    }

    /**
     * Define additional widget. See https://www.algolia.com/doc/guides/building-search-ui/widgets/create-your-own-widgets/js/
     *
     * @param {String} key
     * @param {Function} callback
     */
    defineWidget(key, callback) {
        if (widgets.has(key)) {
            console.warn(`${key} is a reserved widget key or is already added!`)
        } else {
            widgets.add(key, callback);
        }
    }

    /**
     * Add every refinement widget based on \IntelliShop\Search\Support\InstantSearchDefinition::refinements
     *
     * @param {String} containerName selector for container
     * @param {String} prefix prefix for selector of an single refinement
     */
    refinements(containerName, prefix) {
        const me = this;

        this.refinementsMap.forEach((refinement) => {
            if (!widgets.has(refinement.type)) {
                console.warn(refinement.type + ' is not supported yet');
                return;
            }

            const widgetWithPanel = panel({
                templates: {
                    header: refinement.label,
                },
                hidden(options) {
                    if (refinement.hideIfNoOptionsAreAvailable === false) {
                        return false;
                    }

                    return options.items?.length === 0;
                },
                collapsed: () => refinement.initialCollapsed ?? false
            })(widgets.get(refinement.type));

            let instantSearchConfig = refinement.instantSearchConfig ?? {};

            if (instantSearchConfig.hasOwnProperty('tooltips') && instantSearchConfig.tooltips) {
                instantSearchConfig.tooltips = {
                    format(value) {
                        return me.transform(refinement, value);
                    },
                }
            }

            const widgetParams = {
                ...instantSearchConfig,
                container: `#${prefix}${refinement.attribute}`,
                transformItems: function (items) {
                    return items.map(item => me.transformItem(refinement, item));
                }
            };

            this.search.addWidgets([
                widgetWithPanel(widgetParams),
            ])
        })
    }

    /**
     * Mutate a refinement item.
     *
     * @param {Object} refinement Refinement definition (over PHP)
     * @param {Object} item Refinement item (over response)
     */
    transformItem(refinement, item) {
        const me = this;

        return {
            ...item,
            data: item.hasOwnProperty('data') && item.data !== null ? item.data.map(data => me.transformItem(refinement, data)) : null,
            label: me.transform(refinement, item.value),
        }
    }

    /**
     * Mutate a refinement value.
     *
     * @param {Object} refinement Refinement definition (over PHP)
     * @param {String} value value to mutate
     */
    transform(refinement, value) {
        if (refinement.hasOwnProperty('mutators')) {
            return _reduce(refinement.mutators, (value, m) => {
                if (mutators.has(m.method)) {
                    return mutators.get(m.method)(value, m.config ?? {});
                } else {
                    console.warn(m.method + ' is not a supported mutator yet');
                }

                return value;
            }, value)
        }

        return value;
    }

    configureCategoryFilter(current_category_id, children_categories) {

        children_categories.push(current_category_id);

        this.search.addWidgets([
            configure({
                "filters": '('+children_categories.map(function(category_id){
                    return 'categories: ' + category_id
                }).join(' OR ') + ')'
            })
        ]);
    }

    categories(containerName, attribute, categories, name, filter_categories_facet, children_categories, current_category_id) {
        const categories_widget = panel({
            templates: {header: name},
            hidden(options) {
                return options.items?.length === 1;
            },
/*

            collapsed: () => refinement.initialCollapsed ?? false

             */
        })(menu);

        this.search.addWidgets([
            categories_widget({
                container: containerName,
                attribute: attribute,
                limit: 100,
                transformItems(items) {
                    var result = items.filter(function(item){
                        if(!filter_categories_facet){
                            return true;
                        }
                        return children_categories.includes(parseInt(item.value));
                    }).map(function (item) {
                        item.label = categories[parseInt(item.value)].name;
                        item.image = categories[parseInt(item.value)].image;
                        return item;
                    });
                    if(result.length == 0){
                        document.getElementById('categories_facet').style.display = 'none';
                    }
                    return result;
                },
                templates: {
                    item(data, {html}) {
                        const {label, count, url, cssClasses, isRefined, image} = data;

                        var is_selected = isRefined ? 'is_selected' : '';

                        return html`
                            <a class="categories_facet_link ${is_selected}" href="${url}">
                                <span class="categories_facet_image"><img data-src="${image}" class="Sirv" alt="${label}" style="width: 50px;height: 30px;" /></span>
                                <span class="categories_facet_label"><span>${label}</span></span>
                                <span class="categories_facet_count"><span class="counter">${count.toLocaleString()}</span></span>
                            </a>
                        `;

                    }
                }
            })
        ])
    }

    /**
     * Add searchBox widget. See https://www.algolia.com/doc/api-reference/widgets/search-box/js/
     *
     * @param {String} containerName selector for container
     */
    searchBox(containerName) {
        this.search.addWidgets([
            searchBox({
                container: containerName,
                showLoadingIndicator: true,
            })
        ])
    }

    /**
     * Add sortBy widget. See https://www.algolia.com/doc/api-reference/widgets/sort-by/js/
     *
     * @param {String} containerName selector for container
     */
    sortBy(containerName) {
        if (this.config.sorting.length) {
            this.search.addWidgets([
                sortBy({
                    container: containerName,
                    items: this.config.sorting
                })
            ]);
        }
    }

    /**
     * Add hits widget. See https://www.algolia.com/doc/api-reference/widgets/hits/js/
     *
     * @param {String} containerName selector for container
     * @param {String} emptyResult used for empty result
     */
    hits(containerName, emptyResult) {
        this.containerName = containerName;
        this.search.addWidgets([
            hits({
                container: containerName,
                templates: {
                    item: `{{{parsedTemplate}}}`,
                    empty: emptyResult,
                },
            }),
        ])
    }

    /**
     * Add stats widget. See https://www.algolia.com/doc/api-reference/widgets/stats/js/
     *
     * @param {String} containerName selector for container
     * @param {String} text
     */
    stats(containerName, text) {
        this.search.addWidgets([
            stats({
                container: containerName,
                templates: {
                    text: text
                        .replace(/\:query/g, '{{query}}')
                        .replace(/\:nbSortedHits/g, '{{#helpers.formatNumber}}{{nbSortedHits}}{{/helpers.formatNumber}}')
                        .replace(/\:nbHits/g, '{{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}}')
                }
            })
        ]);
    }

    /**
     * Add hits per page widget. See https://www.algolia.com/doc/api-reference/widgets/hits-per-page/js/
     *
     * @param {String} containerName selector for container
     */
    hitsPerPage(containerName) {
        if (this.config.itemsPerPage.length > 1) {
            this.search.addWidgets([
                hitsPerPage({
                    container: containerName,
                    items: this.config.itemsPerPage ?? [],
                })
            ]);
        }
    }

    /**
     * Add currentRefinements widget. See https://www.algolia.com/doc/api-reference/widgets/current-refinements/js/
     *
     * @param {String} containerName selector for container
     */
    currentRefinements(containerName) {
        const me = this;

        const excludedAttributes = _flatMap(
            Array.from(me.refinementsMap.values()).filter((ref) => ref.hideFromCurrentFilters === true && ref.hasOwnProperty('instantSearchConfig')),
            (ref) => {
                if (ref.instantSearchConfig.hasOwnProperty('attributes')) {
                    return ref.instantSearchConfig.attributes;
                }

                return [ref.instantSearchConfig.attribute];
            }
        );
        excludedAttributes.push('query');

        //exclude categories because of special facet
        excludedAttributes.push('categories');

        this.search.addWidgets([
            currentRefinements({
                container: containerName,
                excludedAttributes,
                transformItems(items, {results}) {
                    return items.map((item) => {
                        const searchKey = item.attribute.includes('.lvl') ? item.attribute.split('.')[0] : item.attribute;

                        if (!me.refinementsMap.has(searchKey)) {
                            return item;
                        }
                        const refinement = me.refinementsMap.get(searchKey);

                        return {
                            ...item,
                            label: refinement.label,
                            refinements: item.refinements.map((r) => me.transformItem(refinement, r))
                        }
                    })
                },
            }),
        ])
    }

    /**
     * Add pagination widget. See https://www.algolia.com/doc/api-reference/widgets/pagination/js/
     *
     * @param {String} containerName selector for container
     */
    pagination(containerName) {
        this.search.addWidgets([
            pagination({
                container: containerName,
            }),
        ])
    }

    /**
     * Add clearRefinements widget. See https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
     *
     * @param {String} containerName selector for container
     * @param {String} label Text for the label
     */
    clearRefinements(containerName, label) {
        this.search.addWidgets([
            clearRefinements({
                container: containerName,
                templates: {
                    resetLabel: label
                }
            })
        ])
    }

    useLoadingCallback(callback) {
        if (typeof callback !== 'function') {
            console.warn('Loading callback should be a function')
        }
        this.loadingCallback = callback;
    }

    useDefaultLoadingCallback() {
        this.loadingCallback = (isSearchStalled) => loader(isSearchStalled);
    }

    /**
     * Start instantsearch
     */
    start() {
        this.search.on('render', (renderOptions, isFirstRender) => {
            const element = document.querySelector(this.containerName);
            executeHtmlHooks(element);
            checkFilters();
        });
        this.search.start();
    }
}
const cache = new Map;

function checkFilters() {
    // reset
    $('#searchFilter .ais-Panel').each(function (index, element) {
        $(this).find('.ais-Panel-header span').removeClass('has-filters');
        $(element).find('button').removeClass('has-filters');
    });
    // mark selected filters
    $('#searchFilter .ais-Panel:has(.ais-RefinementList-item--selected)').each(function (index, element) {
        $(this).find('.ais-Panel-header span').addClass('has-filters');
        $(element).find('button').addClass('has-filters');
    });
}

(function () {
    this.CreateSearch = function (config) {
        if (!config.hasOwnProperty('indexName')) {
            throw new Error('CreateSearch method needs an object key named \'indexName\'');
        }

        if (!cache.has(config.indexName)) {
            cache.set(config.indexName, new SearchWrapper(config));
        }

        return cache.get(config.indexName);
    };
}.call(window))

